/**
 * §9 readiness dry-run tester — paste an order id, evaluate it against the saved
 * readiness rules, and see READY or the list of failing reasons (REQUIRE/BLOCK +
 * message). Read-only.
 */
import { useState } from "react";
import { ApiError, api, type AdminReadinessRule, type ReadinessTestResult } from "../../../lib/api.js";
import { Button, Icon } from "../../../components/ui/index.js";

export function ReadinessTester({ rules, onClose }: { rules: AdminReadinessRule[]; onClose: () => void }) {
  const [orderId, setOrderId] = useState("");
  const [result, setResult] = useState<ReadinessTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  async function run() {
    const id = orderId.trim();
    if (!id) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.admin.testReadiness(id, rules);
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404 ? "No order with that id." : "Couldn’t run the test.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="tester">
      <div className="tester__bar">
        <Icon name="bolt" size={15} />
        <span className="tester__title">Readiness tester</span>
        <input
          className="control tester__input"
          placeholder="Order id (cuid)"
          aria-label="Order id"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void run();
          }}
        />
        <Button variant="primary" size="sm" icon="bolt" onClick={() => void run()} disabled={running || !orderId.trim()}>
          {running ? "Running…" : "Run"}
        </Button>
        <button type="button" className="tester__close" aria-label="Close tester" onClick={onClose}>
          <Icon name="x" size={14} />
        </button>
      </div>

      {error && <p className="tester__error">{error}</p>}

      {result && (
        <div className="tester__result">
          <div className="tester__summary">
            <span className="muted">
              {result.order.name} · {result.order.storeRegion} · {result.order.financialStatus ?? "—"}
            </span>
            {result.result.ready ? (
              <span className="tester__outcome tester__outcome--ok">
                <Icon name="check" size={14} /> READY
              </span>
            ) : (
              <span className="tester__outcome tester__outcome--fail">
                <Icon name="alert" size={14} /> Not ready · {result.result.reasons.length} reason
                {result.result.reasons.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          {result.result.reasons.length > 0 && (
            <ul className="tester__trace">
              {result.result.reasons.map((r) => (
                <li key={r.ruleId} className="trace-row">
                  <span className={`chip ${r.effect === "BLOCK" ? "chip--exception" : "chip--hold"}`}>{r.effect}</span>
                  <span className="trace-row__name">{r.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
